import { isLeft } from "fp-ts/Either";
import * as t from "io-ts";
import { type SyntheticEvent, useState } from "react";
import { t as ttag } from "ttag";

import { loadCSRFToken } from "@thelabnyc/thelabui/src/utils/api";

const ApiErrorResponse = t.record(t.string, t.array(t.string));

const parseResponseErrors = (
    body: unknown,
): { nonField: string | null; fields: Record<string, string[]> } => {
    const decoded = ApiErrorResponse.decode(body);
    const raw = isLeft(decoded)
        ? { non_field_errors: [ttag`Something went wrong. Please try again.`] }
        : decoded.right;
    let nonField: string | null = null;
    const fields: Record<string, string[]> = {};
    for (const [key, messages] of Object.entries(raw)) {
        if (key === "non_field_errors") {
            nonField = messages[0] ?? null;
        } else {
            fields[key] = messages;
        }
    }
    return { nonField, fields };
};

// null means "success without a server response to parse" (e.g. no-op submit path)
type SubmitFn = (data: FormData) => Promise<Response | null>;

interface UseFormSubmitResult {
    isSubmitting: boolean;
    nonFieldError: string | null;
    fieldErrors: Record<string, string[]>;
    handleSubmit: (e: SyntheticEvent<HTMLFormElement>) => Promise<void>;
}

type FormState =
    | { status: "idle" }
    | { status: "submitting" }
    | {
          status: "error";
          nonFieldError: string | null;
          fieldErrors: Record<string, string[]>;
      };

export function useFormSubmit(
    submit: SubmitFn,
    onSuccess?: () => void,
): UseFormSubmitResult {
    const [state, setState] = useState<FormState>({ status: "idle" });

    const handleSubmit = async (e: SyntheticEvent<HTMLFormElement>) => {
        e.preventDefault();
        const data = new FormData(e.currentTarget); // capture before any await — React nullifies currentTarget after yield
        setState({ status: "submitting" });

        const token = await loadCSRFToken().catch(() => null);
        if (token) {
            data.set("csrfmiddlewaretoken", token);
        }

        let response: Response | null;
        try {
            response = await submit(data);
        } catch {
            setState({
                status: "error",
                nonFieldError: ttag`Something went wrong. Please try again.`,
                fieldErrors: {},
            });
            return;
        }

        if (response !== null && !response.ok) {
            let body: unknown;
            try {
                body = await response.json();
            } catch {
                setState({
                    status: "error",
                    nonFieldError: ttag`Something went wrong. Please try again.`,
                    fieldErrors: {},
                });
                return;
            }
            const { nonField, fields } = parseResponseErrors(body);
            setState({
                status: "error",
                nonFieldError: nonField,
                fieldErrors: fields,
            });
            return;
        }

        setState({ status: "idle" });
        onSuccess?.();
    };

    const isSubmitting = state.status === "submitting";
    const nonFieldError = state.status === "error" ? state.nonFieldError : null;
    const fieldErrors = state.status === "error" ? state.fieldErrors : {};

    return { isSubmitting, nonFieldError, fieldErrors, handleSubmit };
}
